import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, FileText, Image, X, ChevronDown, ChevronUp, Package, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { getAdminCaseStudies, createCaseStudy, updateCaseStudy, deleteCaseStudy, uploadFile, getProducts } from '../../services/api';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CaseStudiesAdmin = () => {
  const [studies, setStudies] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: '', project_name: '', description: '', results: '',
    highlights: [''], products: [{ product_name: '', component_name: '', material: '', product_id: '' }],
    trial_reports: [], cover_image: '', category: '', is_active: true, order: 0
  });

  const fetchData = async () => {
    try {
      const [studiesRes, productsRes] = await Promise.all([
        getAdminCaseStudies(),
        getProducts({ limit: 200 })
      ]);
      setStudies(studiesRes.data || []);
      setProducts(productsRes.data?.products || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_BASE}${url}`;
  };

  const resetForm = () => ({
    title: '', project_name: '', description: '', results: '',
    highlights: [''], products: [{ product_name: '', component_name: '', material: '', product_id: '' }],
    trial_reports: [], cover_image: '', category: '', is_active: true, order: 0
  });

  const openCreate = () => {
    setEditing(null);
    setForm(resetForm());
    setDialogOpen(true);
  };

  const openEdit = (study) => {
    setEditing(study);
    setForm({
      title: study.title || '',
      project_name: study.project_name || '',
      description: study.description || '',
      results: study.results || '',
      highlights: study.highlights?.length ? [...study.highlights] : [''],
      products: study.products?.length ? study.products.map(p => ({ ...p })) : [{ product_name: '', component_name: '', material: '', product_id: '' }],
      trial_reports: study.trial_reports || [],
      cover_image: study.cover_image || '',
      category: study.category || '',
      is_active: study.is_active !== false,
      order: study.order || 0
    });
    setDialogOpen(true);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file, 'case-studies');
      setForm(f => ({ ...f, cover_image: res.data.url || res.data.file_url }));
      toast.success('Cover image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleReportUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const res = await uploadFile(file, 'case-studies');
        urls.push(res.data.url || res.data.file_url);
      }
      setForm(f => ({ ...f, trial_reports: [...f.trial_reports, ...urls] }));
      toast.success(`${urls.length} report(s) uploaded`);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const removeReport = (index) => {
    setForm(f => ({ ...f, trial_reports: f.trial_reports.filter((_, i) => i !== index) }));
  };

  // Highlights management
  const addHighlight = () => setForm(f => ({ ...f, highlights: [...f.highlights, ''] }));
  const updateHighlight = (i, val) => {
    const h = [...form.highlights];
    h[i] = val;
    setForm(f => ({ ...f, highlights: h }));
  };
  const removeHighlight = (i) => setForm(f => ({ ...f, highlights: f.highlights.filter((_, idx) => idx !== i) }));

  // Products management
  const addProduct = () => setForm(f => ({ ...f, products: [...f.products, { product_name: '', component_name: '', material: '', product_id: '' }] }));
  const updateProduct = (i, field, val) => {
    const p = [...form.products];
    p[i] = { ...p[i], [field]: val };
    setForm(f => ({ ...f, products: p }));
  };
  const removeProduct = (i) => setForm(f => ({ ...f, products: f.products.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...form,
      highlights: form.highlights.filter(h => h.trim()),
      products: form.products.filter(p => p.product_name.trim())
    };
    try {
      if (editing) {
        await updateCaseStudy(editing.id, data);
        toast.success('Case study updated');
      } else {
        await createCaseStudy(data);
        toast.success('Case study created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this case study?')) return;
    try {
      await deleteCaseStudy(id);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const active = studies.filter(s => s.is_active).length;

  return (
    <div className="space-y-6" data-testid="case-studies-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Case Studies</h1>
          <p className="text-sm text-gray-500">{active} active case studies</p>
        </div>
        <Button onClick={openCreate} className="bg-[#2E3A6F]" data-testid="add-case-study-btn">
          <Plus size={16} className="mr-2" /> Add Case Study
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-6 h-28" /></Card>)}</div>
      ) : studies.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-12 text-center text-gray-500"><FileText className="mx-auto mb-3 text-gray-300" size={40} /><p>No case studies yet. Add your first trial report and project case study.</p></CardContent></Card>
      ) : (
        <div className="space-y-4">
          {studies.map(study => (
            <Card key={study.id} className={`hover:shadow-md transition-shadow ${!study.is_active ? 'opacity-60' : ''}`} data-testid={`case-study-admin-card-${study.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Thumbnail */}
                  <div className="w-20 h-20 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                    {study.cover_image ? (
                      <img src={getImageUrl(study.cover_image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><FileText size={24} className="text-gray-300" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-gray-900 truncate">{study.title}</h3>
                      {!study.is_active && <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Inactive</span>}
                    </div>
                    <p className="text-sm text-[#2E3A6F] font-medium">{study.project_name}</p>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                      {study.category && <span className="bg-gray-100 px-2 py-0.5 rounded">{study.category}</span>}
                      <span className="flex items-center gap-1"><Package size={11} /> {study.products?.length || 0} products</span>
                      <span className="flex items-center gap-1"><FileText size={11} /> {study.trial_reports?.length || 0} reports</span>
                      <span className="flex items-center gap-1">{study.highlights?.length || 0} highlights</span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(study)}><Pencil size={14} className="mr-1" /> Edit</Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(study.id)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Case Study</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="e.g. High Speed Milling Trial at XYZ Plant" data-testid="cs-title-input" /></div>
              <div><Label>Project Name *</Label><Input value={form.project_name} onChange={e => setForm({ ...form, project_name: e.target.value })} required placeholder="e.g. Project Alpha" data-testid="cs-project-input" /></div>
              <div><Label>Category</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="e.g. Automotive, Aerospace" data-testid="cs-category-input" /></div>
            </div>

            <div><Label>Description *</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required rows={4} placeholder="Describe the project, challenge, and solution..." data-testid="cs-description-input" /></div>

            <div><Label>Results / Outcome</Label><Textarea value={form.results} onChange={e => setForm({ ...form, results: e.target.value })} rows={3} placeholder="Describe results achieved..." data-testid="cs-results-input" /></div>

            {/* Cover Image */}
            <div>
              <Label>Cover Image</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.cover_image && (
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden border">
                    <img src={getImageUrl(form.cover_image)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, cover_image: '' }))} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5"><X size={12} /></button>
                  </div>
                )}
                <label className="cursor-pointer">
                  <div className="border-2 border-dashed rounded-lg px-4 py-3 text-center hover:bg-gray-50 transition-colors">
                    <Image size={20} className="mx-auto text-gray-400 mb-1" />
                    <p className="text-xs text-gray-500">{uploading ? 'Uploading...' : 'Upload Cover'}</p>
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={uploading} />
                </label>
              </div>
            </div>

            {/* Trial Reports */}
            <div>
              <Label>Trial Reports (PDFs / Images)</Label>
              <div className="space-y-2 mt-1">
                {form.trial_reports.map((report, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <FileText size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate flex-1">Report {i + 1}</span>
                    <button type="button" onClick={() => removeReport(i)} className="text-red-500 hover:text-red-700"><X size={14} /></button>
                  </div>
                ))}
                <label className="cursor-pointer block">
                  <div className="border-2 border-dashed rounded-lg px-4 py-3 text-center hover:bg-gray-50 transition-colors">
                    <Upload size={20} className="mx-auto text-gray-400 mb-1" />
                    <p className="text-xs text-gray-500">{uploading ? 'Uploading...' : 'Upload Trial Reports (PDF/Images)'}</p>
                  </div>
                  <input type="file" accept="image/*,.pdf" multiple className="hidden" onChange={handleReportUpload} disabled={uploading} />
                </label>
              </div>
            </div>

            {/* Highlights */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Key Highlights</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addHighlight} className="text-xs h-7"><Plus size={12} className="mr-1" /> Add</Button>
              </div>
              <div className="space-y-2">
                {form.highlights.map((h, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={h} onChange={e => updateHighlight(i, e.target.value)} placeholder={`e.g. 40% improvement in tool life`} className="flex-1" />
                    {form.highlights.length > 1 && (
                      <button type="button" onClick={() => removeHighlight(i)} className="text-red-500 hover:text-red-700"><X size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Products */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Products Used</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addProduct} className="text-xs h-7"><Plus size={12} className="mr-1" /> Add Product</Button>
              </div>
              <div className="space-y-3">
                {form.products.map((p, i) => (
                  <div key={i} className="border rounded-lg p-3 space-y-2 relative">
                    {form.products.length > 1 && (
                      <button type="button" onClick={() => removeProduct(i)} className="absolute top-2 right-2 text-red-500 hover:text-red-700"><X size={14} /></button>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2">
                        <Input value={p.product_name} onChange={e => updateProduct(i, 'product_name', e.target.value)} placeholder="Product Name *" list={`product-suggestions-${i}`} />
                        <datalist id={`product-suggestions-${i}`}>
                          {products.map(prod => <option key={prod.id} value={prod.name} />)}
                        </datalist>
                      </div>
                      <Input value={p.component_name} onChange={e => updateProduct(i, 'component_name', e.target.value)} placeholder="Component Name" />
                      <Input value={p.material} onChange={e => updateProduct(i, 'material', e.target.value)} placeholder="Material" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Display Order</Label>
                <Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="flex items-end gap-3 pb-1">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
                <Label>Active (visible on website)</Label>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="cs-submit-btn" disabled={uploading}>
                {editing ? 'Update' : 'Create'} Case Study
              </Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseStudiesAdmin;
